/**
 * Die WebSocket-Nachrichten (§48).
 *
 * §48 verlangt eine Realtime-Technologie und listet auf, **was** synchronisiert werden muss.
 * Wie die Nachrichten heißen und geschnitten sind, ist eine technische Entscheidung
 * (`CLAUDE.md`, „Technische Fragen entscheidest du selbst"). Vier Entscheidungen stecken darin:
 *
 * **1. Der Client sendet Absichten, der Server Zustände** (Regel 1). Das ist am Protokoll
 * ablesbar und wird von einem Test strukturell geprüft: keine eingehende Variante trägt ein
 * Brett, eine Figurenposition, einen Punktestand oder eine Uhr. Ein manipulierter Client kann
 * nichts *behaupten*, nur etwas *wollen* — und was er will, prüft der Server gegen seine eigene
 * Aktionsliste.
 *
 * **2. Der Sitzungs-Token kommt als erste Nachricht, nicht in der URL.** URLs landen in
 * Server-Logs, im `Referer`-Header und in der Browser-Historie (`CLAUDE.md`, Security by
 * Design). Ein Token im Query-String wäre damit an drei Stellen gespeichert, die niemand
 * aufräumt. Deshalb `AUTHENTICATE` — und deshalb `redactForLog`, denn geloggt wird vor allem
 * das, was die Validierung **abgewiesen** hat.
 *
 * **3. Fehler tragen Codes, nicht nur Texte.** Ein Client soll auf „du bist nicht am Zug"
 * anders reagieren als auf „zu viele Anfragen", und zwar ohne Zeichenketten zu vergleichen.
 * Texte sind für Menschen, Codes für Programme — und die Texte gehen später durch i18n
 * (Regel 6), die Codes nie.
 *
 * **4. `COMMIT` nennt die Action ID.** Sie wäre technisch entbehrlich, weil der Server die
 * offene Preview kennt. Sie ist aber die Zusicherung „ich bestätige *das*, was ich sehe": ein
 * Client mit veralteter Anzeige — nach einem Reconnect, nach einer verschluckten Nachricht —
 * kann damit nicht etwas verbindlich machen, was der Spieler längst nicht mehr vor sich hat.
 * §34 verlangt ohnehin eine erneute Validierung; diese ID macht sie auch zu einer Prüfung
 * gegen die **Absicht** und nicht nur gegen die Legalität.
 *
 * **Die Lobby kam in Zyklus 8g dazu** — als **eine** Nachricht, die dieselbe Ansicht trägt wie
 * `GET /api/v1/games/lobby`. Kein eigenes WS-Format und keine Einzelnachrichten für „Farbe
 * gewählt", „Team geändert", „Spieler entfernt": der Host schickt eine vollständige
 * Konfiguration (Nr. 27), also ist die vollständige Ansicht auch die natürliche Antwort. Eine
 * Nachricht je Teiländerung wäre ein zweites Modell derselben Lobby, und der Client müsste
 * Deltas zusammensetzen, die der Server ohnehin schon fertig hat. Ein „Ready" gibt es nicht
 * (Nr. 28).
 *
 * **Die Aufstellung kam in Zyklus 9c dazu** (§10, §11) — drei Absichten hinein, **eine**
 * Nachricht hinaus. Die Aufteilung ist dieselbe wie bei `STATE`/`CLOCK`: die Positionen reisen
 * in der projizierten `STATE`-Nachricht an ihren Besitzer, die Zahlen in `SETUP` an den Raum.
 * Eine gesetzte Figur ändert für die anderen nur eine Zahl, und ihnen den ganzen Zustand zu
 * schicken wäre nicht nur Verschwendung, sondern die Gelegenheit, versehentlich einen
 * unprojizierten zu schicken.
 *
 * Verdrahtet ist das seit Zyklus 9d (`apps/server/src/realtime/session.ts`); das Protokoll ist
 * damit vollständig — sieben Absichten hinein, neun Nachrichten hinaus.
 */
import { z } from 'zod';
import { gameEventSchema } from './game-events.js';
import { legalActionSchema } from './legal-actions.js';
import { gameStateSchema } from './game-state.js';
import { lobbyViewSchema } from './rest-dtos.js';
/** Eine Axialkoordinate in einer Nachricht. */
const hexSchema = z.strictObject({ q: z.number().int(), r: z.number().int() });
const playerIdSchema = z.string().min(1);
const actionIdSchema = z.string().min(1);
/**
 * Eine optionale Kennung, die der Server in `ERROR` zurückgibt.
 *
 * Ohne sie könnte ein Client mit zwei offenen Absichten nicht erkennen, welche abgewiesen
 * wurde — und würde im Zweifel beide verwerfen oder beide stehen lassen.
 */
const requestIdSchema = z.string().min(1).max(64).optional();
// ---------------------------------------------------------------------------
// Client → Server: Absichten
// ---------------------------------------------------------------------------
const authenticateSchema = z.strictObject({
    type: z.literal('AUTHENTICATE'),
    /** Der Sitzungs-Token aus dem Beitritt (§46). Gehört **nie** in ein Log. */
    sessionToken: z.string().min(1),
    requestId: requestIdSchema,
});
const setPreviewSchema = z.strictObject({
    type: z.literal('SET_PREVIEW'),
    /**
     * Die Kennung einer Aktion aus der **eigenen** legalen Liste (§30, §31). Der Server schlägt
     * sie in seiner selbst berechneten Liste nach — eine fremde ID findet sich dort nicht und
     * ist damit nicht einschleusbar (§110).
     */
    actionId: actionIdSchema,
    requestId: requestIdSchema,
});
const cancelPreviewSchema = z.strictObject({
    type: z.literal('CANCEL_PREVIEW'),
    requestId: requestIdSchema,
});
const commitSchema = z.strictObject({
    type: z.literal('COMMIT'),
    /** Siehe Modulkopf, Punkt 4: die Bestätigung nennt, *was* bestätigt wird. */
    actionId: actionIdSchema,
    requestId: requestIdSchema,
});
/**
 * Eine Figur in der Aufstellung setzen (§10, Nr. 32).
 *
 * **Eine Absicht für Setzen und Umsetzen.** Woher die Figur kommt — aus dem Vorrat oder von
 * einem anderen Feld —, weiß der Server; ein `from` wäre eine Behauptung des Clients über den
 * Zustand und damit nach Regel 1 verboten. Ob das Feld in der eigenen Startzone liegt und frei
 * ist, prüft die Engine.
 */
const placePieceSchema = z.strictObject({
    type: z.literal('PLACE_PIECE'),
    pieceId: z.string().min(1),
    to: hexSchema,
    requestId: requestIdSchema,
});
/** Eine gesetzte Figur zurück in den Vorrat nehmen (Nr. 32). */
const unplacePieceSchema = z.strictObject({
    type: z.literal('UNPLACE_PIECE'),
    pieceId: z.string().min(1),
    requestId: requestIdSchema,
});
/**
 * „Aufstellung fertig" (§10).
 *
 * Trägt **nichts** — die Aufstellung liegt längst beim Server, Figur für Figur. Eine
 * mitgeschickte Formation wäre ein Zustand und damit eine Behauptung (Regel 1). Der Server
 * weist ab, wenn noch Figuren fehlen (Nr. 34) oder schon bestätigt ist (Nr. 33).
 */
const confirmSetupSchema = z.strictObject({
    type: z.literal('CONFIRM_SETUP'),
    requestId: requestIdSchema,
});
/**
 * Alles, was ein Client schicken darf.
 *
 * `discriminatedUnion` und nicht `union`: der Fehler nennt dann das falsche Feld statt alle
 * Varianten aufzuzählen, und das Parsen ist nicht von der Reihenfolge abhängig.
 */
export const clientMessageSchema = z.discriminatedUnion('type', [
    authenticateSchema,
    setPreviewSchema,
    cancelPreviewSchema,
    commitSchema,
    placePieceSchema,
    unplacePieceSchema,
    confirmSetupSchema,
]);
// ---------------------------------------------------------------------------
// Server → Client: Zustand und Ereignisse
// ---------------------------------------------------------------------------
/** Die Gründe, aus denen der Server eine Absicht ablehnt. */
export const WS_ERROR_CODES = [
    /** Keine oder eine ungültige Sitzung — auch für widerrufene und fremde Tokens. */
    'UNAUTHENTICATED',
    /** Die Absicht kam von jemandem, der nicht am Zug ist (§50). */
    'NOT_YOUR_TURN',
    /** Die Action ID steht nicht in der legalen Liste dieses Spielers (§110). */
    'ILLEGAL_ACTION',
    /** Ein Commit ohne offene Preview — den gibt es nicht (§29, §34). */
    'NO_PREVIEW',
    /** Die Partie ist nicht im Status PLAYING (§40, §47). */
    'GAME_NOT_PLAYING',
    /**
     * Die Partie ist nicht in der Aufstellungsphase.
     *
     * Eigener Code neben `GAME_NOT_PLAYING`, weil der Client verschieden reagieren muss: dort
     * heißt es „die Partie läuft noch nicht", hier „die Aufstellung ist vorbei".
     */
    'GAME_NOT_IN_SETUP',
    /** Dieser Spieler hat seine Aufstellung bestätigt und ändert sie nicht mehr (Nr. 33). */
    'SETUP_CONFIRMED',
    /** Bestätigen geht erst, wenn alle Figuren stehen (Nr. 34). */
    'SETUP_INCOMPLETE',
    /** Zu viele Nachrichten. Preview-Spam ist ein echter Vektor (`MULTIPLAYER.md`). */
    'RATE_LIMITED',
    /** Die Nachricht passte nicht auf das Schema. */
    'INVALID_MESSAGE',
    /** Alles andere. Trägt nach außen bewusst keine Details. */
    'INTERNAL',
];
/**
 * Bestätigt die Sitzung. Trägt **keinen** Token zurück — der geht einmal hin und nie zurück,
 * sonst stünde er in jedem Client-Log.
 */
const welcomeSchema = z.strictObject({
    type: z.literal('WELCOME'),
    gameId: z.string().min(1),
    playerId: playerIdSchema,
});
/**
 * Der vollständige Zustand **aus der Perspektive des Empfängers** (§45, §46, Nr. 26).
 *
 * `viewer` steht dabei, obwohl der Client weiß, wer er ist: so ist eine falsch adressierte
 * Projektion am Client erkennbar, statt sich als rätselhaft unvollständiges Brett zu zeigen.
 * Die Projektion selbst liegt im Server (`apps/server/src/realtime/projection.ts`).
 */
const stateSchema = z.strictObject({
    type: z.literal('STATE'),
    viewer: playerIdSchema,
    state: gameStateSchema,
    /**
     * Ist diese Partie **regelwidrig erzeugt** worden? (Nr. 38, **E39**, Zyklus 11g)
     *
     * Die Markierung lebt an der Partie und nicht im Zustand — die Engine kennt keine Herkunft —,
     * reist aber im Umschlag neben dem Zustand mit, so wie `viewer`. Daraus macht die Oberfläche
     * ein **Abzeichen am Statusbereich**: Nr. 38 verlangt, dass man eine erzwungene Stellung
     * sieht und nicht nur in der Historie findet. Der Schaden entsteht dadurch, dass jemand eine
     * Beobachtung aus ihr Tage später für echt hält, und dafür ist eine Zeile tief in der
     * Historie zu leise.
     *
     * **Pflichtangabe, nicht optional.** Fehlte sie, wäre „die Partie ist sauber" von „der Dienst
     * hat es nicht mitgeschickt" nicht zu unterscheiden — und das Abzeichen fehlte
     * stillschweigend. Genau das schließt Nr. 38 aus.
     *
     * **Nicht dasselbe wie `debugTouchedAt`:** dass jemand einen *regelkonformen* Eingriff
     * gemacht hat, ist eine Angabe für die Auswertung (Debug-Ansicht) und keine für die
     * Mitspieler. Hier steht nur, was die Stellung unglaubwürdig macht.
     */
    ruleOverridden: z.boolean(),
});
/**
 * Die öffentliche Preview (§29, §36) — oder `null`, wenn niemand etwas erwägt.
 *
 * Spieler und Action ID stehen schon **in** der Aktion (`player`, `id`); ein zusätzlicher
 * Umschlag darum wäre eine zweite Quelle für dieselbe Angabe.
 */
const previewSchema = z.strictObject({
    type: z.literal('PREVIEW'),
    preview: legalActionSchema.nullable(),
});
/** Ein permanentes Ereignis der Zughistorie (§56). Preview-Ereignisse gehören hier nicht hin. */
const eventSchema = z.strictObject({
    type: z.literal('EVENT'),
    event: gameEventSchema,
});
/**
 * Die **vollständige** Zughistorie, beim Anmelden und beim Reconnect (§46, §67 Schritt 47).
 *
 * Nötig, weil `EVENT` nur das Neue meldet: wer die Seite neu lädt, hätte sonst eine leere
 * Historie vor sich, obwohl der Server sie vollständig gespeichert hat. Eine eigene Nachricht und
 * kein Feld in `STATE`, weil die Historie nicht zum Zustand gehört — sie wächst neben ihm, wird
 * nie projiziert (sie ist öffentlich) und soll nicht bei jedem Zug mitreisen.
 *
 * **Sie ersetzt, sie ergänzt nicht.** Der Client setzt seine Liste auf diese — ein Anhängen
 * würde nach einem Reconnect Ereignisse verdoppeln.
 */
const historySchema = z.strictObject({
    type: z.literal('HISTORY'),
    events: z.array(gameEventSchema),
});
/**
 * Die Uhren (§48, „Timer").
 *
 * Eigene Nachricht und nicht Teil von `STATE`, weil die Uhr tickt, ohne dass sich der Zustand
 * ändert — den vollständigen Zustand sekündlich zu senden wäre Verschwendung. `at` nennt den
 * Zeitpunkt, auf den sich die Werte beziehen, damit der Client lokal weiterzählen kann, ohne
 * zu raten.
 */
const clockSchema = z.strictObject({
    type: z.literal('CLOCK'),
    remaining: z.record(playerIdSchema, z.number().int().min(0)),
    /** Wessen Uhr läuft, oder `null` — in `PAUSED` und am Partieende läuft keine (Nr. 26). */
    running: playerIdSchema.nullable(),
    at: z.number().int(),
});
/** Wer verbunden ist (§48: Join, Disconnect, Reconnect). */
const presenceSchema = z.strictObject({
    type: z.literal('PRESENCE'),
    connected: z.array(playerIdSchema),
});
/**
 * Die Lobby (§48) — **dieselbe Ansicht wie über HTTP**.
 *
 * Geht nach **jeder** Änderung an die ganze Partie: Konfiguration, Beitritt, Zuordnung,
 * Entfernen, Start. Keine Projektion je Empfänger, und das ist eine Feststellung und keine
 * Auslassung: die Ansicht enthält nichts, was vor einem Beteiligten verborgen wäre. Die
 * Geheimhaltung aus §45 betrifft die **Aufstellung**, nicht die Lobby.
 *
 * **`connected` wird von `PRESENCE` fortgeschrieben**, nicht von einer neuen Lobby-Nachricht.
 * Dieselbe Aufteilung wie bei `STATE` und `CLOCK`: eine Verbindung kommt und geht, ohne dass
 * sich die Lobby ändert — die ganze Ansicht dafür erneut zu schicken wäre Verschwendung. Der
 * Wert in dieser Nachricht ist der Stand im Moment des Versands.
 */
const lobbySchema = z.strictObject({
    type: z.literal('LOBBY'),
    lobby: lobbyViewSchema,
});
/**
 * Der öffentliche Stand der Aufstellung (§10, §11, Nr. 35).
 *
 * **Wie viele, nicht wo.** Jeder sieht, wer bestätigt hat und wie viele Figuren schon stehen;
 * die Positionen bleiben bis zum Partiebeginn geheim (§10) und reisen ausschließlich in der
 * projizierten `STATE`-Nachricht an ihren Besitzer. Ein Feld in dieser Nachricht wäre der Bruch
 * genau dieser Zusage — ein Test prüft deshalb, dass eine Koordinate darin abgewiesen wird.
 *
 * Eigene Nachricht und nicht Teil von `STATE`, aus demselben Grund wie bei `CLOCK`: eine
 * gesetzte Figur ändert für die **anderen** nur eine Zahl. Ihnen den ganzen Zustand zu
 * schicken, der aus ihrer Sicht unverändert ist, wäre Verschwendung.
 */
const setupSchema = z.strictObject({
    type: z.literal('SETUP'),
    progress: z.array(z.strictObject({
        player: playerIdSchema,
        placed: z.number().int().min(0),
        total: z.number().int().min(0),
        confirmed: z.boolean(),
    })),
    /** Wann die Aufstellungszeit abläuft, oder `null` für unbegrenzt (§42). */
    deadlineAt: z.number().int().nullable(),
});
const errorSchema = z.strictObject({
    type: z.literal('ERROR'),
    code: z.enum(WS_ERROR_CODES),
    /** Für Menschen. Geht später durch i18n (Regel 6) — der Code nie. */
    message: z.string().min(1),
    /** Die `requestId` der abgewiesenen Absicht, wenn sie eine hatte. */
    requestId: z.string().min(1).max(64).optional(),
});
export const serverMessageSchema = z.discriminatedUnion('type', [
    welcomeSchema,
    stateSchema,
    previewSchema,
    eventSchema,
    historySchema,
    clockSchema,
    presenceSchema,
    lobbySchema,
    setupSchema,
    errorSchema,
]);
// ---------------------------------------------------------------------------
// Parsen in beide Richtungen
// ---------------------------------------------------------------------------
/** Prüft eine eingehende Nachricht. Wirft — der Aufrufer antwortet mit `INVALID_MESSAGE`. */
export function parseClientMessage(raw) {
    return clientMessageSchema.parse(raw);
}
/**
 * Prüft eine ausgehende Nachricht.
 *
 * Das wirkt überflüssig — der Server baut sie ja selbst. Es ist aber genau die Prüfung, die
 * einen Fehler **dort** findet, wo er entsteht: eine fehlerhafte Servernachricht zeigt sich
 * sonst als unverständlicher Fehler im Client, und gesucht wird an der falschen Stelle
 * (`CLAUDE.md` Regel 7: in beide Richtungen validiert).
 */
export function parseServerMessage(raw) {
    return serverMessageSchema.parse(raw);
}
/**
 * Entschärft eine Nachricht fürs Log.
 *
 * Nimmt **`unknown`** und nicht `ClientMessage`: geloggt wird vor allem, was die Validierung
 * abgewiesen hat, und dort ist die Form unbekannt — gerade dort kann aber ein Token stecken.
 * Eine Funktion, die nur gültige Nachrichten entschärfen kann, wäre an der einzigen Stelle
 * nutzlos, an der sie gebraucht wird.
 */
export function redactForLog(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        return raw;
    }
    const entries = Object.entries(raw).map(([key, value]) => SECRET_KEYS.has(key) ? [key, '[redacted]'] : [key, value]);
    return Object.fromEntries(entries);
}
/** Felder, die nie in ein Log gehören. */
const SECRET_KEYS = new Set([
    'sessionToken',
    'inviteToken',
    'hostToken',
    'token',
    'adminKey',
]);
//# sourceMappingURL=ws-messages.js.map